import type { LucideIcon } from "lucide-react";
import {
  Asterisk,
  Bot,
  Braces,
  Code2,
  Equal,
  Gauge,
  Hash,
  Import,
  Route,
  ScanSearch,
  Shield,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";

import { ScorecardPrimitiveType } from "@/queries/smart-tables/scorecard";

export interface CriterionCatalogItem {
  primitiveType: ScorecardPrimitiveType | string;
  label: string;
  description: string;
  icon: LucideIcon;
  implemented: boolean;
}

export interface CriterionCatalogCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  items: CriterionCatalogItem[];
}

export const CRITERION_CATALOG: CriterionCatalogCategory[] = [
  {
    id: "quality",
    label: "Quality",
    icon: Sparkles,
    items: [
      {
        primitiveType: ScorecardPrimitiveType.COLUMN_AGGREGATE,
        label: "Column Evaluator",
        description: "Create one scorecard-wide result from a column’s values.",
        icon: Import,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.LLM_ASSERTION,
        label: "AI Judge",
        description: "Uses an LLM to grade each row against a rubric.",
        icon: Sparkles,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.CODE_EXECUTION,
        label: "Code Check",
        description: "Runs a deterministic script against the output.",
        icon: Code2,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.COMPARE,
        label: "Compare Values",
        description: "Compares text, numbers, or JSON against an expected column.",
        icon: Equal,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.CONTAINS,
        label: "Contains Text",
        description: "Checks that the output contains a required string.",
        icon: ScanSearch,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.REGEX,
        label: "Regex Match",
        description: "Tests the output against a regular expression.",
        icon: Asterisk,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.ASSERT_VALID,
        label: "Valid Format",
        description: "Asserts the output parses as a valid format.",
        icon: Braces,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.COUNT,
        label: "Count Check",
        description: "Passes when character/word/sentence/paragraph count is within bounds.",
        icon: Hash,
        implemented: true,
      },
    ],
  },
  {
    id: "agent-behavior",
    label: "Agent Behavior",
    icon: Bot,
    items: [
      {
        primitiveType: ScorecardPrimitiveType.MAX_TOOL_CALLS,
        label: "Max Tool Calls",
        description: "Limits the total number of tool calls the agent may make.",
        icon: Gauge,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.NO_INVALID_TOOL_CALLS,
        label: "No Invalid Tool Calls",
        description: "Flags any tool call in the trace with invalid arguments.",
        icon: ShieldCheck,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.TRAJECTORY,
        label: "Trajectory Check",
        description: "Checks that the agent followed an expected tool call sequence.",
        icon: Route,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.TOOL_EVALUATOR,
        label: "Tool Evaluator",
        description: "Grades how well the agent selected and called tools.",
        icon: Wand2,
        implemented: true,
      },
      {
        primitiveType: ScorecardPrimitiveType.STRUCTURE,
        label: "Structure Check",
        description: "Validates the output matches an expected structure.",
        icon: Shield,
        implemented: true,
      },
    ],
  },
];

export const getCriterionCatalogItem = (
  primitiveType: string,
): CriterionCatalogItem | undefined => {
  for (const category of CRITERION_CATALOG) {
    const match = category.items.find(item => item.primitiveType === primitiveType);
    if (match) {
      return match;
    }
  }
  return undefined;
};

export const getCriterionIcon = (primitiveType: string): LucideIcon =>
  getCriterionCatalogItem(primitiveType)?.icon ?? Gauge;
